import React, { useState, useEffect } from "react";
import "../JobPosting/jobs.scss";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Snackbar } from "@mui/material";
import {
  fetchEvaluationVariants,
  createEvaluationVariant,
  updateEvaluationVariant,
  deleteEvaluationVariant,
} from "../../Redux/ActionCreators/jobsAction";

const EvaluationFit = ({ setType, jobId: propJobId }) => {
  const { jobId: paramsJobId } = useParams();
  const jobId = propJobId || paramsJobId;
  const dispatch = useDispatch();

  const variants = useSelector((state) => state.jobs.evaluationVariants || []);

  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", label_instructions: "" });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const loadVariants = async () => {
    setLoading(true);
    await dispatch(fetchEvaluationVariants(jobId));
    setLoading(false);
  };

  useEffect(() => {
    if (jobId) loadVariants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const showSnackbar = (message, severity = "error") => {
    setSnackbar({ open: true, message, severity });
  };

  const resetForm = () => {
    setForm({ name: "", label_instructions: "" });
    setEditId(null);
    setShowForm(false);
  };

  const handleAddNewClick = () => {
    setForm({ name: "", label_instructions: "" });
    setEditId(null);
    setShowForm(true);
  };

  const handleEditClick = (variant) => {
    setForm({ name: variant.name || "", label_instructions: variant.label_instructions || "" });
    setEditId(variant.id);
    setShowForm(true);
  };

  const handleDuplicateClick = (variant) => {
    const baseNameMatch = (variant.name || "").match(/^(.*) - duplicate(?: \((\d+)\))?$/);
    const baseName = baseNameMatch ? baseNameMatch[1] : variant.name || "";

    const existingNames = variants.map((v) => v.name);
    let newName = `${baseName} - duplicate`;
    if (existingNames.includes(newName)) {
      let counter = 2;
      while (existingNames.includes(`${baseName} - duplicate (${counter})`)) {
        counter++;
      }
      newName = `${baseName} - duplicate (${counter})`;
    }

    setForm({ name: newName, label_instructions: variant.label_instructions || "" });
    setEditId(null);
    setShowForm(true);
  };

  const handleDelete = async (variantId) => {
    setDeletingId(variantId);
    const result = await dispatch(deleteEvaluationVariant(jobId, variantId));
    setDeletingId(null);
    if (result?.success) {
      await loadVariants();
      showSnackbar("Evaluation fit deleted successfully", "success");
    } else {
      showSnackbar(result?.message || "Failed to delete evaluation fit due to duplicate");
    }
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.label_instructions.trim()) {
      showSnackbar("Name and label instructions are mandatory!", "error");
      return;
    }

    setSaveLoading(true);
    let result;
    if (editId) {
      result = await dispatch(
        updateEvaluationVariant(jobId, editId, { label_instructions: form.label_instructions })
      );
    } else {
      result = await dispatch(
        createEvaluationVariant(jobId, {
          name: form.name,
          label_instructions: form.label_instructions,
          display_order: variants.length + 1,
        })
      );
    }
    setSaveLoading(false);

    if (result?.success) {
      await loadVariants();
      showSnackbar(editId ? "Evaluation fit updated successfully!" : "Evaluation fit added successfully!", "success");
      resetForm();
    } else if (editId) {
      showSnackbar(result?.message || "Failed to update evaluation fit");
    } else {
      showSnackbar("Evaluation variant with this name already exists");
    }
  };

  return (
    <div className="job-prescreening">
      <div className="job-settings-head">
        <img
          src="/images/back-arrow-black.svg"
          alt="back"
          width={32}
          height={32}
          onClick={() => (showForm ? resetForm() : setType(""))}
          style={{ cursor: "pointer" }}
        />
        <div className="job-settings-head-title">
          <h3>Job Variants</h3>
          <p>{variants.length} variant{variants.length === 1 ? "" : "s"}</p>
        </div>
      </div>

      {!showForm && (
        <div className="job-settings-body">
          {loading ? (
            <div
              className="loading-overlay"
              style={{ position: "relative", marginTop: 16, alignItems: "start", height: "32px" }}
            >
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="evaluation-fit-grid">
              {variants.map((variant) => (
                <div className="evaluation-fit-card" key={variant.id}>
                  <div className="evaluation-fit-card-header">
                    <h4 title={variant.name}>{variant.name}</h4>
                    <div className="evaluation-fit-card-icons">
                      <button onClick={() => handleEditClick(variant)} title="Edit">
                        <img className="edit-icon" src="/images/square-pen-black.svg" alt="edit" width={20} height={20} />
                      </button>
                      <button onClick={() => handleDuplicateClick(variant)} title="Duplicate">
                        <img className="duplicate-icon" src="/images/duplicate-icon.svg" alt="duplicate" width={20} height={20} />
                      </button>
                      <button onClick={() => handleDelete(variant.id)} title="Delete" disabled={deletingId === variant.id}>
                        <img className="delete-icon" src="/images/trash-bin-black.svg" alt="delete" width={20} height={20} />
                      </button>
                    </div>
                  </div>
                  <p>{variant.label_instructions}</p>
                </div>
              ))}

              <div className="evaluation-fit-card evaluation-fit-card--add" onClick={handleAddNewClick}>
                <span className="evaluation-fit-card--add-icon">+</span>
                <span>Add new variant</span>
              </div>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div className="job-settings-form">
          <div className="job-settings-form-question">
            <label>Name</label>
            <textarea
              placeholder="Enter name"
              value={form.name}
              disabled={!!editId}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div className="job-settings-form-question">
            <label>Label Instructions</label>
            <textarea
              className="label-instructions-textarea"
              placeholder="Enter label instructions"
              value={form.label_instructions}
              rows={3}
              onChange={(e) => setForm({ ...form, label_instructions: e.target.value })}
            />
          </div>
          <div className="d-flex gap-4 align-items-center">
            <button className="save-btn" type="button" onClick={handleSave} disabled={saveLoading}>
              {saveLoading ? "Saving..." : "Save"}
            </button>
            <h6 className="cursor-pointer cancle-txt" onClick={resetForm}>Cancel</h6>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default EvaluationFit;
